import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Initialize Sentry for error tracking
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE || "development",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: import.meta.env.PROD,
  sendDefaultPii: true,
});

// =============================================================================
// Global Error Handlers
// =============================================================================
// These catch errors that happen outside of React's error boundary system,
// including chunk load failures that occur before React mounts.

/**
 * Check if an error is a chunk load failure
 */
function isChunkLoadError(error: Error | string): boolean {
  const message = typeof error === "string" ? error : error?.message || "";
  return (
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Unable to preload CSS") ||
    message.includes("Failed to load module script") ||
    message.includes("ChunkLoadError")
  );
}

/**
 * Show a fallback error UI when React can't render
 */
function showFallbackErrorUI(isChunkError: boolean, errorMessage: string) {
  const root = document.getElementById("root");
  if (!root) return;

  root.innerHTML = `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 24px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #0a0a0a;
      color: #fafafa;
    ">
      <div style="
        max-width: 400px;
        width: 100%;
        background: #171717;
        border: 1px solid #262626;
        border-radius: 12px;
        overflow: hidden;
      ">
        <div style="padding: 24px; text-align: center; border-bottom: 1px solid #262626;">
          <div style="
            width: 48px;
            height: 48px;
            margin: 0 auto 16px;
            background: rgba(239, 68, 68, 0.1);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <path d="M12 9v4"/>
              <path d="M12 17h.01"/>
            </svg>
          </div>
          <h1 style="font-size: 20px; font-weight: 600; margin: 0;">
            ${isChunkError ? "App Update Available" : "Something Went Wrong"}
          </h1>
          <p style="font-size: 14px; color: #a1a1aa; margin: 8px 0 0;">
            ${
              isChunkError
                ? "A new version of the app is available. Please reload to continue."
                : "The app encountered an unexpected error."
            }
          </p>
        </div>
        <div style="padding: 16px; background: rgba(38, 38, 38, 0.5);">
          <p style="font-size: 12px; color: #71717a; text-align: center; font-family: monospace; word-break: break-word; margin: 0;">
            ${errorMessage.substring(0, 150)}${errorMessage.length > 150 ? "..." : ""}
          </p>
        </div>
        <div style="padding: 16px;">
          <button
            onclick="window.location.href = window.location.href.split('?')[0] + '?_=' + Date.now();"
            style="
              width: 100%;
              padding: 12px 16px;
              background: #3b82f6;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
              <path d="M8 16H3v5"/>
            </svg>
            Reload App
          </button>
        </div>
      </div>
    </div>
  `;
}

// Handle uncaught errors (errors that escape React)
window.addEventListener("error", (event) => {
  const error = event.error;

  // Log for debugging
  console.error("[Global Error Handler]", error);

  // If it's a chunk load error and React hasn't rendered yet, show fallback UI
  if (isChunkLoadError(error)) {
    event.preventDefault();
    showFallbackErrorUI(true, error?.message || "Failed to load app resources");
  }
});

// Handle unhandled promise rejections (async errors)
window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason;

  // Log for debugging
  console.error("[Unhandled Rejection]", error);

  // If it's a chunk load error, show fallback UI
  if (isChunkLoadError(error)) {
    event.preventDefault();
    showFallbackErrorUI(true, error?.message || "Failed to load app resources");
  }
});

// =============================================================================
// App Mount
// =============================================================================

const rootElement = document.getElementById("root");

if (rootElement) {
  try {
    createRoot(rootElement).render(<App />);
  } catch (error) {
    console.error("[Mount Error]", error);
    const err = error as Error;
    showFallbackErrorUI(
      isChunkLoadError(err),
      err?.message || "Failed to start app",
    );
  }
} else {
  console.error("[Mount Error] Root element not found");
}
